import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * ISSUE LINK API CLIENT
 *
 * Axios instance configured for linking/unlinking Jira issues to test scenarios.
 * Uses same JWT authentication pattern as other API clients.
 */
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all outgoing requests.
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token:', error)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * RESPONSE INTERCEPTOR
 * Handles global error responses, especially authentication failures.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      console.error('Unauthorized access - clearing token cache')
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// ISSUE LINK API FUNCTIONS
// ============================================================================

/**
 * Fetch all test scenarios linked to a Jira issue.
 * Returns scenario details with per-run execution status counts.
 *
 * @param {string} jiraIssueKey - Jira issue key (e.g. "PROJ-123")
 * @returns {Promise<Array>} Array of scenario objects with runs and status counts
 */
export const fetchLinkedScenarios = async (jiraIssueKey) => {
  try {
    const response = await apiClient.get(`/jira-issues/${jiraIssueKey}/testscenarios`)
    return response.data
  } catch (error) {
    console.error('Error fetching linked scenarios:', error)
    throw error
  }
}

/**
 * Bulk link a Jira issue to multiple test scenarios.
 * Duplicates (already linked) are silently skipped.
 *
 * @param {Object} data - { testScenarioIds: number[], jiraIssueKey: string }
 * @returns {Promise<Array>} Array of newly created link records
 */
export const bulkLinkScenarios = async (data) => {
  try {
    const response = await apiClient.post('/jira-issues/bulk-link', data)
    return response.data
  } catch (error) {
    console.error('Error bulk linking scenarios:', error)
    throw error
  }
}

/**
 * Unlink a specific Jira issue from a test scenario.
 *
 * @param {number} testScenarioId - ID of the test scenario
 * @param {number} linkId - ID of the link record to remove
 * @returns {Promise<void>}
 */
export const unlinkScenario = async (testScenarioId, linkId) => {
  try {
    await apiClient.delete(`/testscenarios/${testScenarioId}/jira-issues/${linkId}`)
  } catch (error) {
    console.error('Error unlinking scenario:', error)
    throw error
  }
}

/**
 * Link a single Jira issue to a test scenario.
 * Creates a new link record between the scenario and the Jira issue.
 *
 * @param {number} testScenarioId - ID of the test scenario
 * @param {string} jiraIssueKey - Jira issue key (e.g., "PROJ-123")
 * @returns {Promise<Object>} Created link record { id, jiraIssueKey, jiraIssueId, linkedByUserId, linkedOn }
 */
export const linkJiraIssueToScenario = async (testScenarioId, jiraIssueKey) => {
  try {
    const response = await apiClient.post(
      `/testscenarios/${testScenarioId}/jira-issues`,
      { jiraIssueKey }
    )
    return response.data
  } catch (error) {
    console.error('Error linking Jira issue to scenario:', error)
    throw error
  }
}

/**
 * Fetch all Jira issues linked to a specific test scenario.
 * Used to find the link ID for a given issue key when unlinking.
 *
 * @param {number} testScenarioId - ID of the test scenario
 * @returns {Promise<Array>} Array of link records { id, jiraIssueKey, jiraIssueId, linkedByUserId, linkedOn }
 */
export const fetchLinkedIssuesForScenario = async (testScenarioId) => {
  try {
    const response = await apiClient.get(`/testscenarios/${testScenarioId}/jira-issues`)
    return response.data
  } catch (error) {
    console.error('Error fetching linked issues for scenario:', error)
    throw error
  }
}
